/* Field park: the markdown answer sits in the field anatomy until the editor
 * round's markdown field (field-wrapper.md, "Retiring Field"). */
import type { FieldControlProps } from '@main/complykit';
import { Field } from '@main/complykit';
import { DynamicFieldState, EditableTextareaFieldConfig } from '@main/dynamic-form';
import { MarkdownEditor } from '@main/ui';
import { Controller, FieldValues, useFormContext } from 'react-hook-form';

import { NotAnswered } from './not-answered';

type MarkdownFieldProps<TSchema extends FieldValues> = {
  config: EditableTextareaFieldConfig<TSchema>;
  /** The engine's state, when the editor is a registered field. */
  state?: DynamicFieldState;
  /** The engine's id for the element that names the field (form-engine.md). */
  labelId?: string;
  /** A ComplyKit Field's control, where the editor sits in a Field outside the engine. */
  control?: FieldControlProps;
  isDisabled?: boolean;
  isReadOnly?: boolean;
};

export function MarkdownField<TSchema extends FieldValues = FieldValues>({
  state,
  labelId,
  ...props
}: MarkdownFieldProps<TSchema>) {
  if (!state) {
    return <MarkdownControl {...props} />;
  }

  const name = labelId
    ? { 'aria-labelledby': labelId }
    : { label: props.config.label ?? props.config.name };

  return (
    <Field
      {...name}
      isRequired={state.isRequired}
      isReadOnly={state.isReadOnly}
      isDisabled={state.isDisabled}
      error={state.error}
    >
      {(control) => (
        <MarkdownControl
          config={props.config}
          control={control}
          isDisabled={state.isDisabled}
          isReadOnly={state.isReadOnly}
        />
      )}
    </Field>
  );
}

function MarkdownControl<TSchema extends FieldValues>({
  config,
  control: fieldControl,
  isDisabled = false,
  isReadOnly = false,
}: Omit<MarkdownFieldProps<TSchema>, 'state' | 'labelId'>) {
  const { control } = useFormContext();

  const shouldRenderText = isDisabled || isReadOnly;

  return (
    <Controller
      name={config.name}
      control={control}
      rules={config.validation}
      defaultValue={config.value}
      render={({ field }) => {
        const value: unknown = field.value;

        if (shouldRenderText && !(typeof value === 'string' && value.trim())) {
          return <NotAnswered />;
        }

        return (
          <MarkdownEditor
            key={`${config.name}-${shouldRenderText ? 'text' : 'editor'}`}
            value={field.value}
            config={{ editable: !shouldRenderText }}
            boxProps={shouldRenderText ? readTextBoxProps : undefined}
            onValueChange={(v) => field.onChange(v)}
            onBlur={field.onBlur}
            variant={shouldRenderText ? 'unstyled' : 'textarea'}
            contentEditableProps={{
              ...fieldControl,
              // Lexical drops a read-only editor from the tab order; a read-only answer stays reachable.
              tabIndex: isReadOnly && !isDisabled ? 0 : undefined,
              autoFocus: false,
              'data-testid': `${config.label}-content-editable`,
            }}
            placeholder={config.placeholder}
          />
        );
      }}
    />
  );
}

/* A value read as text takes a field value's type, 14/20 at full contrast,
 * not the unstyled editor's 12px. */
const readTextBoxProps = {
  color: 'var(--ck-text-default)',
  sx: { '& > .editor-input': { fontSize: '0.875rem', lineHeight: '1.25rem' } },
};
